import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import SignupPage from './signup'
import AuthOtpPage from './otp'
import SigninPage from './signin'
import ForgetPassword from './forget-password'
import Toast from '../../components/modals/Toast'
import { ThemeProvider } from 'styled-components'
import { useMainContext } from '../../core/contexts/main'
import { getMainTheme } from '../../core/utils/theme'
import { HOME } from '../../core/constants/urls'
import { SubmitBtn, TabChanger } from '../../styles/newStyles/Register.styled'
import Text from '../../core/utils/Text'
import { useQueryContext } from '../../core/contexts/query'
import {
	useRequestForgetPasswordOtp,
	useRequestOtp,
	useVerify2fa,
	useVerifyForgetPasswordOtp,
	useVerifyOtp,
} from '../../core/services/react-query/useAuthQuery'
import { LoaderContainer } from '../../styles/CommonStyles'
import { PulseLoader } from 'react-spinners'
import ModalLayout from '../../components/layouts/ModalLayout'
import { AuthBody } from '../../styles/layout-styles/CommonStyles'
import LeftIcon from '../../components/icons/Left'
import { FaArrowRight } from 'react-icons/fa6'
import LangSwitcher from '../../components/common/LangSwitcher'
import { p2e } from '../../core/utils/common'
import { useAuthRoute } from './core/useAuthRoute'
import { useAuthForm } from './core/useAuthForm'
import { useEmailValidation } from './core/useEmailValidation'
import { useOtpTimer } from './core/useOtpTimer'
import { useDeviceInfo } from './core/useDeviceInfo'
import { useAuthSubmit } from './core/useAuthSubmit'
import { AUTH_STEPS, VALIDATION_STATUS } from './core/constants'

// Component mapping for each auth step
const AUTH_COMPONENTS = [
	SignupPage,
	AuthOtpPage,
	SigninPage,
	AuthOtpPage,
	ForgetPassword,
]

export default function AuthPage() {
	const {
		main: { theme, lang },
		profile: { token },
	} = useMainContext()
	const themeObject = useMemo(() => getMainTheme(theme, lang), [theme, lang])

	const navigate = useNavigate()
	const { activeStep, activePath } = useAuthRoute()
	const { setToast } = useQueryContext()

	// Form state management
	const { formState, setEmail, setAffiliateCode, toggleRulesAccepted, resetForm } = useAuthForm()

	// Validation
	const {
		validationStatus,
		signMethod,
		validateInput,
		setValidationStatus,
	} = useEmailValidation()

	// OTP timer
	const { timer, startTimer, canResend } = useOtpTimer()

	// Device info
	const deviceInfo = useDeviceInfo()

	// Local state
	const [maintenanceModal, setMaintenanceModal] = useState(false)
	const [verifyCode, setVerifyCode] = useState('')
	const [password, setPassword] = useState('')
	const [authCode, setAuthCode] = useState('')
	const otpInputRef = useRef()
	const lastHandledOtpRef = useRef(null)
	const captchaToken = null

	// URL params
	const [searchParams] = useSearchParams()
	const usernameParam = searchParams.get('username')
	const affiliateParam = searchParams.get('affiliate')
	const redirect = searchParams.get('redirect')

	// Redirect if already authenticated
	useEffect(() => {
		if (token) {
			navigate('/')
		}
	}, [navigate, token])

	// Initialize form from URL params
	useEffect(() => {
		if (usernameParam) {
			const normalizedValue = p2e(usernameParam)
			validateInput(normalizedValue)
			setEmail(normalizedValue)
		}
	}, [usernameParam, validateInput, setEmail])

	useEffect(() => {
		if (affiliateParam) {
			const normalizedValue = p2e(affiliateParam)
			setAffiliateCode(normalizedValue)
		}
	}, [affiliateParam, setAffiliateCode])

	// API hooks
	const {
		data: verifyData,
		isLoading: verifyLoading,
		mutate: verifyRefetch,
	} = useVerifyOtp(redirect)

	const { mutate: _2faRefetch, isLoading: verify2faLoading } = useVerify2fa(redirect)

	const {
		data: forgetPassOtpData,
		isLoading: loadingForgetPassOtpData,
		refetch: forgetpassOtpRefetch,
	} = useRequestForgetPasswordOtp({
		[signMethod]: formState.mobileOrEmail,
	})

	const { mutate: verifyForgetPasswordOtp, isLoading: verifyForgetPassOtpLoading } =
		useVerifyForgetPasswordOtp(() => {
			resetForm()
			navigate('/auth/signin')
			navigate(0)
		})

	const onRequestOtpError = useCallback(() => {
		resetForm()
	}, [resetForm])

	const onOtpSuccess = useCallback(
		(data) => {
			if (data?.success) {
				const isSignup = activeStep === AUTH_STEPS.SIGNUP
				// Only navigate if we are currently on the signup or signin page
				if (activeStep === AUTH_STEPS.SIGNUP || activeStep === AUTH_STEPS.SIGNIN) {
					navigate(`/auth/${isSignup ? 'signup' : 'signin'}/otp`)
					startTimer()
				}
			}
		},
		[activeStep, navigate, startTimer]
	)

	const {
		data: otpData,
		isLoading: otpLoading,
		refetch: otpRefetch,
	} = useRequestOtp(
		{
			[signMethod]: formState.mobileOrEmail,
			captcha: captchaToken,
		},
		onRequestOtpError,
		onOtpSuccess
	)

	// Request forget password OTP when on forget password step
	useEffect(() => {
		if (activeStep === AUTH_STEPS.FORGET_PASSWORD) {
			forgetpassOtpRefetch()
		}
	}, [activeStep, forgetpassOtpRefetch])

	// Loading state
	const isLoading =
		otpLoading ||
		verifyLoading ||
		verify2faLoading ||
		loadingForgetPassOtpData ||
		verifyForgetPassOtpLoading

	// Submit handler
	const { handleSubmit } = useAuthSubmit({
		activeStep,
		formState,
		validationStatus,
		signMethod,
		verifyCode,
		password,
		authCode,
		deviceInfo,
		otpRefetch,
		verifyRefetch,
		_2faRefetch,
		verifyData,
		forgetPasswordOtpVerify: verifyForgetPasswordOtp,
		setToast,
		setValidationStatus,
		isLoading,
	})

	// Input handlers
	const onInputValueChange = (e, type) => {
		const value = e?.target?.value
		if (type === 'main') {
			validateInput(value)
			setEmail(value)
		} else if (type === 'option') {
			setAffiliateCode(value)
		}
	}

	const onVerifyCodeChange = (val) => {
		if (val.length > 6) return
		if (val.length === 6) {
			setValidationStatus(VALIDATION_STATUS.VALID)
		}
		if (validationStatus === VALIDATION_STATUS.VALID && val.length !== 6) {
			setValidationStatus(VALIDATION_STATUS.NORMAL)
		}
		setVerifyCode(val)
	}

	const onPasswordChange = (val) => {
		const onlyEnglishRegex = /^[\x00-\x7F]*$/
		if (!onlyEnglishRegex.test(val)) return

		if (val.length >= 8) {
			setValidationStatus(VALIDATION_STATUS.VALID)
		}
		if (validationStatus === VALIDATION_STATUS.VALID && val.length < 8) {
			setValidationStatus(VALIDATION_STATUS.NORMAL)
		}

		setPassword(val)
	}

	const onOtpResend = () => {
		if (!canResend) return
		otpRefetch()
		startTimer()
	}

	const goToRules = () => {
		window.location.href = HOME + '/terms'
	}

	// Keyboard handler
	useEffect(() => {
		const onEnterClick = (event) => {
			if (event.key === 'Enter') {
				handleSubmit()
			}
		}

		document.addEventListener('keydown', onEnterClick)
		return () => document.removeEventListener('keydown', onEnterClick)
	}, [handleSubmit])

	// Get active component
	const ActiveSubpage = AUTH_COMPONENTS[activeStep]

	// Check if submit button should be active
	const isSubmitActive =
		validationStatus === VALIDATION_STATUS.VALID &&
		(formState.rulesAccepted ||
			activeStep === AUTH_STEPS.SIGNIN ||
			activeStep === AUTH_STEPS.SIGNIN_OTP ||
			activeStep === AUTH_STEPS.FORGET_PASSWORD)

	// Check if back button should be shown
	const showBackButton = activePath !== 'signin' && activePath !== 'signup'

	// Check if tab changer should be shown
	const showTabChanger = activePath === 'signin' || activePath === 'signup'

	return (
		<ThemeProvider theme={themeObject}>
			<AuthBody>
				<div className='flex items-center justify-center flex-col h-screen w-screen gap-5 p-8 lg:p-0 '>
					<a href={HOME} className='flex items-center justify-start w-full sm:w-[448px] gap-2'>
						<span className=' border border-inputBorderDark rounded-lg p-1 xs:border-none xs:p-0'>
							<LeftIcon size={22} color='#B9B7E1' className="rtl:scale-x-[-1]" />
						</span>
						<Text tid={'back-home'} className='text-[#B9B7E1] hidden xs:inline-block' />
					</a>

					<div className=' relative w-full sm:w-[448px] p-4 md:p-8 mx-auto flex flex-col justify-center gap-4 border border-secondaryColor rounded-2xl bg-[#010012]'>
						<div className='absolute ltr:right-0 rtl:left-0 -top-14 !max-w-[150px]'>
							<LangSwitcher />
						</div>
						<Text tid={activePath + "-title"} className=' text-3xl text-center text-[#E9EEFC]' />
						<Text tid={activePath + "-step-hint"} className=' text-center text-secondaryColor' />
						<ActiveSubpage
							authCode={authCode}
							goToRules={goToRules}
							inputValues={formState}
							onCheckBoxClicked={toggleRulesAccepted}
							onInputValueChange={onInputValueChange}
							onOtpResend={onOtpResend}
							onVerifyCodeChange={onVerifyCodeChange}
							otpInputRef={otpInputRef}
							setAuthCode={setAuthCode}
							timer={timer}
							validationStatus={validationStatus}
							verifyCode={verifyCode}
							verifyData={verifyData}
							password={password}
							setPassword={setPassword}
							setValidationStatus={setValidationStatus}
							onPasswordChange={onPasswordChange}
							otpData={otpData}
							mobileOrEmail={formState.mobileOrEmail}
							signMethod={signMethod}
						/>
						<SubmitBtn
							className='shine-btn'
							id={activePath}
							active={isSubmitActive}
							onClick={handleSubmit}
						>
							{isLoading ? (
								<LoaderContainer className='mt-0'>
									<PulseLoader size={6} color='#83a8de' />
								</LoaderContainer>
							) : (
								<>
									<Text tid={'continue'} className='text-white' />
									<FaArrowRight color='#FFFFFF' className='rtl:scale-x-[-1]' />
								</>
							)}
						</SubmitBtn>
						{showBackButton && (
							<SubmitBtn
								className=' !bg-none border border-purpleBorderDark !cursor-pointer'
								onClick={() => navigate(-1)}
							>
								<Text tid={"back"} />
							</SubmitBtn>
						)}
						{showTabChanger && (
							<TabChanger className={'gap-2'}>
								<Text
									tid={activePath === 'signin' ? 'not-have-account' : 'have-account'}
									className='text-secondaryColor'
								/>
								<Link to={`/auth/${activePath === 'signin' ? 'signup' : 'signin'}`} fontSize='1rem'>
									<Text
										className='!text-secondaryColor'
										tid={activePath === 'signin' ? 'create-account' : 'signin'}
									/>
								</Link>
							</TabChanger>
						)}
					</div>
				</div>
			</AuthBody>
			<ModalLayout
				open={maintenanceModal}
				title={"Maintenance!"}
				width={"550px"}
				onClose={() => { }}
			>
				<div className='w-full flex  flex-col gap-1 p-3' style={{ direction: "rtl" }}>
					<Text tid="کاربر گرامی،" className='text-active font-semibold' />
					<Text
						tid="پنل تا اطلاع ثانوی به دلیل بروزرسانی در دسترس نمی‌باشد."
						className='text-active font-semibold'
					/>
				</div>
			</ModalLayout>
			<Toast />
		</ThemeProvider>
	)
}
